import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { inject, isDevMode } from '@angular/core';
import { patchState, signalStore, withHooks, withLinkedState, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { injectNavigationEnd } from 'ngxtension/navigation-end';
import { rxEffect } from 'ngxtension/rx-effect';

type MockState = {
    datamartSegmentationCriteriaMissingDivision: boolean;
    mockList: string[];
    couldThisBeOutOfDateWithMockTable: 'Yes, check table to be sure.';
};

const mockInitialState = {
    datamartSegmentationCriteriaMissingDivision: false,
    mockList: [],
    couldThisBeOutOfDateWithMockTable: 'Yes, check table to be sure.' as const,
};

const MocksStore = signalStore(
    { providedIn: 'root' },
    withDevtools('mocks'),
    withState<MockState>(mockInitialState),
    withMethods(store => ({
        addToMockList: (newMock: string) =>
            patchState(store, { mockList: Array.from(new Set(store.mockList()).add(newMock)) }),
        setDivisionSpecificPageIsErroring: () =>
            patchState(store, { datamartSegmentationCriteriaMissingDivision: true }),
    })),
    // Cheese to see in devtools
    withLinkedState(store => ({
        mockHitsOnPage: () => store.mockList().length,
    })),
    withHooks({
        onInit(store) {
            rxEffect(injectNavigationEnd(), () => {
                patchState(store, mockInitialState);
            });
        },
    })
);

/**
 * @description Syncs mock endpoint related info to the Redux Devtools, per page route, like:
 * - How many times all mocks are hit,
 * - Which mock errors,
 * - When mock is missing for a division, like `/datamart/segmentation/criteria?division=XYZ` (`datamartSegmentationCriteriaMissingDivision: boolean`)
 *
 * This cannot be the end-all-be-all source of truth for mocks since more can be added, BUT this covers known mocks when they are hit.
 */
export function mocksInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    if (isDevMode()) {
        const mockStore = inject(MocksStore);

        const divisionSpecificPath = '/datamart/segmentation/criteria?division=';

        const mockEndpoints = [
            '/config/content-assets',
            '/config/content-assets/1',
            '/config/content-assets/55',
            '/datamart/metadata/cmt?level=1',
            '/datamart/metadata/cmt?level=2',
            '/datamart/metadata/cmt?level=3',
            '/datamart/metadata/cmt?level=4',
            '/datamart/metadata/cmt?level=5',
            '/datamart/metadata/cmt?level=6',
            '/datamart/metadata/cmt?level=7',
            '/datamart/metadata/cmt?level=8',
            '/datamart/metadata/cmt?level=9',
            '/datamart/metadata/lifecycle/rules',
            '/datamart/metadata/picklist?tablename=f_sls_cmt_summ&fieldname=cmt_lvl',
            '/datamart/metadata/picklist?tablename=f_sls_cmt_summ&fieldname=is_self_mfg_flg',
            '/datamart/metadata/picklist?tablename=f_sls_cmt_summ&fieldname=ord_entry_type',
            '/datamart/metadata/picklist?tablename=f_sls_cmt_summ&fieldname=prd_label_type',
            '/datamart/metadata/picklist?tablename=f_sls_cmt_summ&fieldname=ssp_flag',
            '/datamart/metadata/picklist?tablename=f_web_act_segment&fieldname=cmt_lvl',
            '/datamart/metadata/picklist?tablename=t_ds_ies_model_v&fieldname=model_type',
            '/datamart/metadata/picklist?tablename=t_ds_rsd_model&fieldname=model_type',
            '/datamart/metadata/picklist',
            '/datamart/querycount/mrqc',
            '/datamart/querycount/qc?error=true',
            '/datamart/querycount/qc',
            '/datamart/relations/multirectables',
            '/datamart/relations/tables?tablename=t_ds_ies_model_v&fieldname=rec_uniq_id',
            '/datamart/relations/tables?tablename=t_ds_rsd_model&fieldname=sf_acct_id',
            '/datamart/search/cmtsearch?level=1',
            '/datamart/search/cmtsearch?level=2',
            '/datamart/search/cmtsearch?level=3',
            '/datamart/search/cmtsearch?level=4',
            '/datamart/search/cmtsearch?level=5',
            '/datamart/search/cmtsearch?level=6',
            '/datamart/search/cmtsearch?level=7',
            '/datamart/search/cmtsearch?level=8',
            '/datamart/search/cmtsearch?level=9',
            '/datamart/search/typeahead?tablename=d_suplr&fieldname=suplr_nm_nbr',
            '/datamart/search/typeahead?tablename=t_ds_rsd_model&fieldname=model_name',
            '/datamart/search/typeahead',
            '/datamart/search/websearch',
            '/datamart/segmentation/criteria?division=AND',
            '/datamart/segmentation/criteria?division=HMD',
            '/datamart/segmentation/criteria?division=IDD',
            '/datamart/segmentation/criteria?division=IES',
            '/datamart/segmentation/criteria?division=ms2',
            '/datamart/segmentation/criteria?division=MSFresh',
            '/datamart/segmentation/criteria?division=MTD',
            '/datamart/segmentation/criteria?division=MW',
            '/datamart/segmentation/criteria??division=NSD',
            '/datamart/segmentation/criteria?division=RSD',
            '/datamart/segmentation/criteria?division=SAD',
            '/datamart/segmentation/datamodel?division=IES',
            '/datamart/segmentation/datamodel',
            '/datamart/segmentation/mrtablemap',
            '/datamart/segmentation/multirectables',
            '/datamart/talkingpoint/mergefield?tablename=model_contact_details',
            '/datamart/talkingpoint/mergefield?tablename=rsd_cmt_nc_contact_mergefields',
            '/datamart/talkingpoint/mergefield?tablename=sales_merge_fields_data_v',
            '/datamart/talkingpoint/mergefield',
            '/datamart/talkingpoint/mergefieldtable',
            '/datamart/talkingpoint/search',
            '/query/count?error=true',
            '/query/count',
            '/query/segmentation',
            '/salesforce/objects',
            '/salesforce/objects/Play__c/fields',
            '/salesforce/objects/TFS_Recommendation__c/fields',
            '/sf/createplay',
            '/sf/describe/DS_Recommendation__c',
            '/sf/describe/Play__c',
            '/sf/describe/TFS_Recommendation__c',
            '/sf/editplay',
            '/sf/sobjects',
        ];

        return next(req).pipe(
            catchError((error: HttpErrorResponse) => {
                // Log errors
                mockEndpoints.forEach(endpoint => {
                    if (error.url?.includes(endpoint)) {
                        mockStore.addToMockList('ERROR' + error.url);
                    }

                    if (
                        error.url?.includes('/datamart/segmentation/criteria?division') &&
                        !endpoint.includes(divisionSpecificPath)
                    ) {
                        mockStore.addToMockList('ERROR:' + error.url);
                        mockStore.setDivisionSpecificPageIsErroring();
                    }
                });
                // Forward error
                return throwError(error);
            }),
            tap(() => {
                // Log non-error
                mockEndpoints.forEach(endpoint => {
                    if (req.urlWithParams.includes(endpoint)) {
                        mockStore.addToMockList(endpoint);
                    }
                });
            })
        );
    }
    return next(req);
}
